import React, { ReactElement, WeakValidationMap, MouseEventHandler, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Heading from '../Heading';
import Button from '../Button';
import SuccessLarge from '@athena/forge-icons/dist/SuccessLarge';
import AttentionLarge from '@athena/forge-icons/dist/AttentionLarge';
import InfoLarge from '@athena/forge-icons/dist/InfoLarge';
import CriticalLarge from '@athena/forge-icons/dist/CriticalLarge';
import CloseSmall from '@athena/forge-icons/dist/CloseSmall';
import { ForgeIconComponent } from '@athena/forge-icons/dist/ForgeIconProps';
import { forgeClassHelper } from '../utils/classes';
import ForgePropTypes from '../utils/propTypes';
import Overlay, { OverlayProps } from '../Overlay';
import { isUndefined } from '../utils/helpers';
import { themeContextPropTypes, useTheme } from '../Root/ThemeContext';
import { Theme } from '../Root/context';
import { AlertTypes } from '../utils/constants';

export type ModalWidth = 'small' | 'medium' | 'large';

export interface ModalProps extends OverlayProps {
  children?: React.ReactNode;
  className?: string;
  closeButtonAriaLabel?: string;
  alertType?: AlertTypes;
  disableClose?: boolean;
  includeButtonRow?: boolean;
  headerText?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  width?: ModalWidth;
  onPrimaryClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  padded?: boolean;
  show?: boolean;
  theme?: Theme;
}

const classes = forgeClassHelper({ name: 'modal', isPortal: true });

const getAlertIcon = (alertType?: AlertTypes): ForgeIconComponent | null => {
  switch (alertType) {
    case 'critical':
      return CriticalLarge;
    case 'attention':
      return AttentionLarge;
    case 'success':
      return SuccessLarge;
    case 'info':
      return InfoLarge;
    default:
      return null;
  }
};

const Modal = ({
  alertType,
  disableClose = false,
  includeButtonRow = true,
  headerText,
  primaryButtonText = 'OK',
  secondaryButtonText = 'Cancel',
  width = 'small',
  closeButtonAriaLabel = 'Close Modal',
  onPrimaryClick,
  onHide, // eslint-disable-line no-unused-vars
  className,
  children,
  theme: upstreamTheme,
  padded = true,
  show = false,
  ...rest
}: ModalProps): ReactElement => {
  const theme = useTheme(upstreamTheme);
  const handleHide = useCallback((): void => {
    if (onHide && !disableClose) {
      onHide();
    }
  }, [onHide, disableClose]);

  const AlertIcon = useMemo(() => getAlertIcon(alertType), [alertType]);

  return (
    <Overlay
      onHide={handleHide}
      show={show}
      {...rest}
      dialogClass={classes({
        element: 'dialog',
        states: [alertType || ''],
        modifiers: { [width]: true, padded },
      })}
      {...classes({
        extra: className,
        states: [alertType || ''],
        modifiers: [width && width],
        theme,
      })}
    >
      <div
        {...classes({
          element: 'header',
          modifiers: { 'without-close': disableClose },
        })}
      >
        {AlertIcon && <AlertIcon {...classes({ element: 'icon' })} />}
        {headerText && <Heading text={headerText} variant="section" />}
        {!disableClose && (
          <Button
            onClick={handleHide}
            aria-label={closeButtonAriaLabel}
            autoFocus
            icon={CloseSmall}
            size="tiny"
            variant="tertiary"
          />
        )}
      </div>
      {children && <div {...classes({ element: 'body' })}>{children}</div>}
      {includeButtonRow && (
        <div {...classes({ element: 'footer' })}>
          {!disableClose && <Button text={secondaryButtonText} variant="secondary" onClick={handleHide} />}
          <Button text={primaryButtonText} onClick={onPrimaryClick} />
        </div>
      )}
    </Overlay>
  );
};

const ModalPropTypes: WeakValidationMap<ModalProps & { '...rest': unknown }> = {
  /** Title text displayed at the top of the modal */
  headerText: PropTypes.string.isRequired,
  /** Gives a default left border and header with icon that corresponds to status level */
  alertType: ForgePropTypes.alertTypes,
  /** Content to display in modal */
  children: PropTypes.node,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Close button aria-label */
  closeButtonAriaLabel: PropTypes.string,
  /** Remove close button and background close clicking */
  disableClose: PropTypes.bool,
  /** Includes default button row */
  includeButtonRow: PropTypes.bool,
  /**
   * Callback fired before the Modal transitions in. Used to manage other animations while modal is opening.
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group/)
   */
  onEnter: PropTypes.func,
  /**
   * Callback fired after the Modal finishes transitioning in. Used to manage other animations while modal is opening.
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group/)
   **/
  onEntered: PropTypes.func,
  /**
   * Callback fired as the Modal begins to transition in. Used to manage other animations while modal is opening.
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group/)
   */
  onEntering: PropTypes.func,
  /**
   * Callback fired right before the Modal transitions out. Used to manage other animations while modal is closing.
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group/)
   */
  onExit: PropTypes.func,
  /**
   * Callback fired after the Modal finishes transitioning out. Used to manage other animations while modal is closing.
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group/)
   */
  onExited: PropTypes.func,
  /**
   * Callback fired as the Modal begins to transition out. Used to manage other animations while modal is closing.
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group/)
   */
  onExiting: PropTypes.func,
  /**
   * A callback fired when the close button, backdrop, or secondary button is clicked, or escape key is pressed.
   *
   * The onHide callback only signals intent from the Modal, **you must actually set the** `show` **prop to** `false` **for the Modal to close.** Required unless `disableClose` is set.
   */
  onHide: function (props, propName, ...rest) {
    if (!props['disableClose'] && isUndefined(props[propName])) {
      return new Error('onHide is required to close modal');
    }
    return PropTypes.func(props, propName, ...rest);
  },
  /** Callback fired when the primary button is clicked */
  onPrimaryClick: PropTypes.func,
  /** A callback fired when the Modal is opening */
  onShow: PropTypes.func,
  /** If set to true, will provide default padding for content */
  padded: PropTypes.bool,
  /** Text for the primary action button */
  primaryButtonText: PropTypes.string,
  /** Text for the secondary button */
  secondaryButtonText: PropTypes.string,
  /**  Set the visibility of the Modal */
  show: PropTypes.bool,
  /** Theme variables for configuring an instance of Forge. See Root component for more details. */
  theme: themeContextPropTypes,
  /** Width of modal dialog box */
  width: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Passthrough props are passed through to the @restart/ui [Modal](https://react-restart.github.io/ui/Modal) component */
  '...rest': PropTypes.any,
};

Modal.propTypes = ModalPropTypes;

export default Modal;
